import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Button from "../../../../iconosv0/src/components/Button/Button.jsx";
import "./../stylesloin.scss";

export default function ElementResetPassword({
  email,        // required: the address we sent the OTP to
  t,            // optional i18n function
  onDone, 
   eyeClosedIcon,
     eyeOpenIcon      // optional callback after successful reset
}) {
  
 const MIN_LEN = 8;
  // OTP
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const otpRefs = useRef([...Array(6)].map(() => React.createRef()));
  const [verifying, setVerifying] = useState(false);
  const [otpOk, setOtpOk] = useState(false);
  const [msg, setMsg] = useState("");

  // Passwords
  const [showPwd, setShowPwd] = useState(false);
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const newPwdRef = useRef(null);
  const [saving, setSaving] = useState(false);

  const onOtpChange = (v, idx) => {
    if (/^\d?$/.test(v)) {
      const next = [...otp];
      next[idx] = v;
      setOtp(next);
      if (v && idx < 5) otpRefs.current[idx + 1].current?.focus();
    }
  };
  const onOtpPaste = (e) => {
    const digits = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (digits.length === 6) {
      setOtp(digits.split(""));
      otpRefs.current[5].current?.focus();
    }
    e.preventDefault();
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setMsg("");
    const code = otp.join("");
    if (code.length !== 6) return setMsg(t("otpIncomplete"));
    try {
      setVerifying(true);
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/verify-otp`,
        { email, otp: code }
      );
      if (res?.data?.success) {
        setOtpOk(true);
        setTimeout(() => newPwdRef.current?.focus(), 0);
      } else {
        setMsg(t("otpError"));
      }
    } catch (err) {
      setMsg(t("verifyOtpError"));
      console.error(err);
    } finally {
      setVerifying(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setMsg("");

    // --- NEW: length check ---
    if (pwd.length < MIN_LEN) {
      setMsg(t("passwordTooShort"));
      newPwdRef.current?.focus();
      return;
    }

    if (pwd !== pwd2) return setMsg(t("passwordMismatchError"));
    try {
      setSaving(true);
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/reset-password`,
        { email, newPassword: pwd }
      );
      setMsg(res?.data?.message || t("resetOk"));
      onDone?.();
    } catch (err) {
      setMsg(t("resetPasswordError"));
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="auth">
      <span aria-hidden className="page-bottom-glow" />
      <section className="container auth__container">
        <header className="auth__header">
          <h1>{t("enterOtpTitle")}</h1>
          <p>{t("enterOtpSubtitle")} {email}</p>
        </header>

        {!otpOk ? (
          <form className="auth__card" onSubmit={verifyOtp}>
            <div className="otp__grid" onPaste={onOtpPaste}>
              {otp.map((d, i) => (
                <input
                  key={i}
                  ref={otpRefs.current[i]}
                  className="otp__input"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={d}
                  onChange={(e) => onOtpChange(e.target.value, i)}
                  aria-label={t("otpDigit")}
                  onKeyDown={(e) => {
  if (e.key === "Backspace" && !otp[i] && i > 0) {
    otpRefs.current[i-1].current?.focus();
  }

}}
autoComplete="one-time-code"
                />
              ))}
            </div>

          <button type="submit" className="btn-primary w-full" disabled={verifying}>
    {t("verifyOtpButton")}
  </button>
            {msg ? <p className="auth__message">{msg}</p> : null}
          </form>
        ) : (
          <form className="auth__card" onSubmit={resetPassword}>
            <div className="field">
              <label htmlFor="new-pass">{t("passwordLabel")}</label>
              <div className="password-wrap">
                <input
                  ref={newPwdRef}
                  id="new-pass"
                  type={showPwd ? "text" : "password"}
                  placeholder={t("enterNewPasswordPlaceholder")}
                  autoComplete="new-password"
                  required
                  value={pwd}
                  minLength={MIN_LEN} 
                  aria-invalid={pwd.length > 0 && pwd.length < MIN_LEN}
                  onChange={(e) => setPwd(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                />
                <button
                  type="button"
                  className="toggle-pwd"
                  aria-pressed={showPwd}
                  aria-label={showPwd ? t("hidePassword") : t("showPassword")}
                  onClick={() => setShowPwd(s => !s)}
                >
                  {showPwd ? eyeClosedIcon() : eyeOpenIcon()}
                </button>
              </div>
                  {/* Optional inline hint */}
              {pwd && pwd.length < MIN_LEN && (
                <small className="field-hint">
                  {t("passwordTooShort")}
                </small>
              )}
            </div>

            <div className="field">
              <label htmlFor="confirm-pass">{t("confirmPasswordLabel")}</label>
              <div className="password-wrap">
                <input
                  id="confirm-pass"
                  type={showPwd ? "text" : "password"}
                  placeholder={t("confirmNewPasswordPlaceholder")}
                  autoComplete="new-password"
                   minLength={MIN_LEN} 
                   aria-invalid={pwd2.length > 0 && pwd !== pwd2}
                  required
                  value={pwd2}
                  onChange={(e) => setPwd2(e.target.value)}
                />
                <button
                  type="button"
                  className="toggle-pwd"
                  aria-pressed={showPwd}
                  aria-label={showPwd ? t("hidePassword") : t("showPassword")}
                  onClick={() => setShowPwd(s => !s)}
                >
                  {showPwd ? eyeClosedIcon() : eyeOpenIcon()}
                </button>
              </div>
                    {pwd2 && pwd !== pwd2 && (
                <small className="field-hint">
                  {t("passwordMismatchError")}
                </small>
              )}
            </div>

      <button type="submit" className="btn-primary w-full" disabled={saving || pwd.length < MIN_LEN || pwd !== pwd2}  >
    {t("resetPasswordButton")}
  </button>
            {msg ? <p className="auth__message">{msg}</p> : null}
          </form>
        )}
      </section>
    </main>
  );
}
